import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, TextField, Typography, MenuItem, GlobalStyles, Autocomplete, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import DCXToolingService from '../../services/DCXToolingService';

const storedProcedures = [
    {
        label: 'Storefront Preferences',
        queryName: 'GetPreferenceBranch',
        parameterInfo: ['Sku','ShipTo','CompanyCode'],
        inputs: ['SKU', 'Customer ID', 'Company Code']
    },
    {
        label: 'Simple Availability',
        queryName: 'GetSimpleAvailability',
        parameterInfo: ['branch','itemNumber','useBranchStockingType'],
        inputs: ['Branch', 'SKU', 'BoolUseBranchStockingType']
    },
    {
        label: 'Get Kit Item Availability',
        queryName: 'GetKitItemAvailability',
        parameterInfo: ['storefrontId','itemNumber','factorVirtualLocations'],
        inputs: ['StorefrontId', 'SKU', 'BoolFactorVirtualLocations']
    },
    {
        label: 'Get Item Availability (Non-Kit)',
        queryName: 'GetAvailability',
        parameterInfo: ['storefrontId','itemNumber','useBranchStockingType','factorVirtualLocations'],
        inputs: ['StorefrontId', 'SKU', 'BoolUseBranchStockingType', 'BoolFactorVirtualLocations']
    }
];

const generateLightningPath = (length = 400, height = 20, jaggedness = 10, variation = 12) => {
    let path = `M0,${height / 2}`;
    for (let x = 0; x <= length; x += jaggedness) {
        const y = height / 2 + (Math.random() * variation - variation / 2);
        path += ` L${x},${y.toFixed(1)}`;
    }
    return path;
};

const generateVerticalLightningPath = (length = 400, width = 20, jaggedness = 10, variation = 12) => {
    let path = `M${width / 2},0`;
    for (let y = 0; y <= length; y += jaggedness) {
        const x = width / 2 + (Math.random() * variation - variation / 2);
        path += ` L${x.toFixed(1)},${y}`;
    }
    return path;
};



const SprocConsole = () => {
    const [selectedSproc, setSelectedSproc] = useState(storedProcedures[0]);
    const [inputValues, setInputValues] = useState({});
    const [terminalOutput, setTerminalOutput] = useState([]);
    const [lastLineIndex, setLastLineIndex] = useState(0);
    const [queryTriggered, setQueryTriggered] = useState(false);
    const outputRef = useRef();
    const [darkMode, setDarkMode] = useState(false);
    const [effectsEnabled, setEffectsEnabled] = useState(false);
    const [allBranches, setAllBranches] = useState([]);
    const [allStorefronts, setAllStorefronts] = useState([]);
    const [queryText, setQueryText] = useState('Query will appear here after execution.');

    useEffect(() => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [terminalOutput]);
    useEffect(() => {
        DCXToolingService.getUniversalParamaterData().then(data => {
            setAllStorefronts(data.storefronts)
            setAllBranches(data.branches)
        });
    }, []);  

    useEffect(() => {
        DCXToolingService.getQueryText(selectedSproc.queryName).then(data => {
            setQueryText(data);
        });
    }, [selectedSproc]);

    const handleClearConsole = () => {
        setTerminalOutput([]);
        setLastLineIndex(0);
    };

    const handleInputChange = (key, value) => {
        setInputValues(prev => ({ ...prev, [key]: value }));
    };

    const handleRunQuery = async () => {
        setQueryTriggered(true);
        setTimeout(() => setQueryTriggered(false), 1000);

        try {
            let parameterValues = Object.values(inputValues);
            const response = await DCXToolingService.executeQuery(selectedSproc.queryName, parameterValues, selectedSproc.parameterInfo);
            loaded(selectedSproc.label, response);
        } catch (error) {
            loaded(selectedSproc.label, {
                results: [],
                messages: [`Error: ${error.message}`]
            });
        }
    };


    const loaded = (label, data) => {
        const timestamp = new Date().toLocaleString();
        const { results = [], messages = [] } = data ?? {};

        const output = [];

        // Add header
        output.push({ type: 'header', content: `=== ${label} @ ${timestamp} ===` });

        // Add messages
        if (messages.length > 0) {
            output.push(...messages.map(msg => ({ type: 'message', content: `> ${msg}` })));
        }

        // Add results as a table
        if (results.length > 0) {
            output.push({ type: 'table', content: results });
        } else {
            output.push({ type: 'message', content: 'No results returned.' });
        }

        setLastLineIndex(terminalOutput.length);
        setTerminalOutput(prev => [...prev, ...output]);
    };


    return (
        <Box
            p={3}
            sx={{
                backgroundColor: darkMode ? '#0d0d0d' : '#f5f5f5',
                color: darkMode ? '#00ffff' : '#333',
                fontFamily: 'Courier New, monospace',
                minHeight: '100vh',
                transition: 'all 0.3s ease',
            }}
        >           

            <GlobalStyles styles={{
                '@keyframes glowPulse': {
                    '0%': { boxShadow: '0 0 5px #ff00ff' },
                    '50%': { boxShadow: '0 0 20px #ff00ff' },
                    '100%': { boxShadow: '0 0 5px #ff00ff' }
                },
                '@keyframes fadeIn': {
                    '0%': { opacity: 0 },
                    '100%': { opacity: 1 }
                },
                '@keyframes flicker': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.6 }
                }
            }} />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
                variant="h5"
                gutterBottom
                sx={{
                    color: darkMode ? '#ff00ff' : '#333',
                    textShadow: effectsEnabled ? '0 0 5px #ff00ff' : '',
                    marginBottom: '24px'
                }}
            >
                ⚡ Sproc Console
            </Typography>
            <Box display="flex" gap={2} mb={3}>
                <Button
                    variant="outlined"
                    onClick={() => setDarkMode(!darkMode)}
                    sx={{
                        color: darkMode ? '#00ffff' : '#333',
                        borderColor: darkMode ? '#00ffff' : '#333',
                    }}
                >
                    Toggle Dark Mode
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => setEffectsEnabled(!effectsEnabled)}
                    sx={{
                        color: darkMode ? '#00ffff' : '#333',
                        borderColor: darkMode ? '#00ffff' : '#333',
                    }}
                >
                    Toggle Effects
                </Button>
            </Box>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ width: "48%" }}>
            <Autocomplete
                options={storedProcedures}
                getOptionLabel={(option) => option.label}
                value={selectedSproc}
                onChange={(event, newValue) => {
                    if (newValue) {
                        setSelectedSproc(newValue);
                        setInputValues({});
                    } else {
                        setSelectedSproc(null); 
                        setInputValues({});
                    }
                }}

                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Stored Procedure"
                        sx={{
                            mb: 2,
                            input: {
                                color: darkMode ? '#00ffff' : '#333',
                                backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                            },
                            label: {
                                color: darkMode ? '#ff00ff' : '#333',
                            },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                '& fieldset': {
                                    borderColor: darkMode ? '#ff00ff' : '#ccc',
                                },
                                '&:hover fieldset': {
                                    borderColor: darkMode ? '#00ffff' : '#999',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: darkMode ? '#00ffff' : '#666',
                                },
                            },
                        }}
                    />
                )}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                            color: darkMode ? '#00ffff' : '#333',
                            border: '1px solid',
                            borderColor: darkMode ? '#ff00ff' : '#ccc',
                            boxShadow: effectsEnabled ? '0 0 10px #ff00ff' : 'none',
                        },
                    },
                    popper: {
                        sx: {
                            zIndex: 1300,
                        },
                    },
                }}
                sx={{
                    '& .MuiAutocomplete-option': {
                        backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                        color: darkMode ? '#00ffff' : '#000',
                        '&:hover': {
                            backgroundColor: darkMode ? '#333' : '#e0e0e0',
                            color: darkMode ? '#00ffff' : '#000',
                        },
                        '&[aria-selected="true"]': {
                            backgroundColor: darkMode ? '#333' : '#fff',
                            color: darkMode ? '#00ffff' : '#000',
                        },
                    },
                }}


            >
                {storedProcedures.map(sp => (
                    <MenuItem
                        key={sp.label}
                        value={sp.label}
                        sx={{
                            color: '#00ffff',
                            backgroundColor: '#1a1a1a',
                            '&:hover': { backgroundColor: '#2a2a2a', color: '#ff00ff' },
                            '&.Mui-selected': { backgroundColor: '#2a2a2a', color: '#ff00ff' },
                            '&.Mui-selected:hover': { backgroundColor: '#333', color: '#00ffff' }
                        }}
                    >
                        {sp.label}
                    </MenuItem>
                ))}
            </Autocomplete>

            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                {selectedSproc?.inputs?.map(input => (
                    (input === 'Branch' || input === "StorefrontId") ? (
                        <TextField
                            select
                            label={input}
                            value={inputValues[input] || ''}

                            sx={{
                                width: '250px', 
                                '& .MuiOutlinedInput-root': {
                                    height: '40px',
                                    border: "none",
                                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                    '& fieldset': {
                                        borderColor: darkMode ? '#ff00ff' : '#ccc',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: darkMode ? '#00ffff' : '#999',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: darkMode ? '#00ffff' : '#666',
                                    },
                                },
                                '& .MuiSelect-select': {
                                    paddingTop: '10px',
                                    paddingBottom: '10px',
                                    color: darkMode ? '#ff00ff' : '#333',
                                },
                                label: {
                                    color: darkMode ? '#ff00ff' : '#333',
                                },
                            }}
                            onChange={e => handleInputChange(input, e.target.value)}
                        >
                            {input === "Branch"  ? allBranches.map(branch => (
                                    <MenuItem key={branch.id} value={branch.number}>
                                        {branch.number + " - " + branch.description}
                                    </MenuItem>
                                ))
                                : allStorefronts.map(storefront => (
                                    <MenuItem key={storefront.id} value={storefront.id}>
                                        {storefront.displayName}
                                    </MenuItem>
                                ))
                            }
                        </TextField>
                    ) : (
                    input.startsWith('Bool') ? (

                                <TextField
                                    select
                                    key={input}
                                    label={input}
                                    value={inputValues[input] === 1 ? 'true' : inputValues[input] === 0 ? 'false' : ''}
                                    onChange={e => handleInputChange(input, e.target.value === 'true' ? 1 : 0)}
                                    sx={{
                                        width: '250px',
                                        '& .MuiOutlinedInput-root': {
                                            height: '40px',
                                            border: "none",
                                            backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                            '& fieldset': {
                                                borderColor: darkMode ? '#ff00ff' : '#ccc',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? '#00ffff' : '#999',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? '#00ffff' : '#666',
                                            },
                                        },
                                        '& .MuiSelect-select': {
                                            paddingTop: '10px',
                                            paddingBottom: '10px',
                                            color: darkMode ? '#ff00ff' : '#333',
                                        },
                                        label: {
                                            color: darkMode ? '#ff00ff' : '#333',
                                        },
                                    }}
                                >
                                    <MenuItem value="true">True</MenuItem>
                                    <MenuItem value="false">False</MenuItem>
                                </TextField>

                    ) : (
                        <TextField
                            key={input}
                            label={input}
                            value={inputValues[input] ?? ''}
                            onChange={e => handleInputChange(input, e.target.value)}
                            sx={{
                                input: {
                                    color: darkMode ? '#00ffff' : '#333',
                                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                    border: "none"
                                },
                                label: {
                                    color: darkMode ? '#ff00ff' : '#333',
                                },
                                '& .MuiOutlinedInput-root': {
                                    height: '40px',
                                    border: "none",
                                    backgroundColor: darkMode ? '#1a1a1a' : '#fff',
                                    '& fieldset': {
                                        borderColor: darkMode ? '#ff00ff' : '#ccc',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: darkMode ? '#00ffff' : '#999',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: darkMode ? '#00ffff' : '#666',
                                    }
                                }
                            }}
                        />
                    )
                )))}
                <Button
                    variant="contained"
                    onClick={handleRunQuery}
                    sx={{
                        backgroundColor: darkMode ? "#ff00ff" : '#118ABD',
                        color: darkMode ? "white" : 'black',
                        textTransform: 'none',
                        boxShadow: effectsEnabled ? '0 0 10px #ff00ff' : "",
                        animation: effectsEnabled ? 'glowPulse 2s infinite' : "",
                        '&:hover': {
                            backgroundColor: '#00ffff',
                            boxShadow: '0 0 10px #00ffff',
                            color: '#0d0d0d'
                        }
                    }}
                >
                    Run Query
                </Button>

                <Button
                    variant="contained"
                    onClick={handleClearConsole}
                    sx={{
                        backgroundColor: darkMode ? "#ff00ff" : '#118ABD',
                        color: darkMode ? "white" : 'black',
                        textTransform: 'none',
                        boxShadow: effectsEnabled ? '0 0 10px #ff00ff' : "",
                        animation: effectsEnabled ? 'glowPulse 2s infinite' : "",
                        '&:hover': {
                            backgroundColor: '#00ffff',
                            boxShadow: '0 0 10px #00ffff',
                            color: '#0d0d0d'
                        }
                    }}
                >
                    Clear Console
                </Button>

            </Box>
                </div>
                <div style={{ width:"48%" }}>
            <Box
                sx={{
                    width: '100%',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: darkMode ? '#1a1a1a' : '#f5f5f5',
                    color: darkMode ? '#00ffff' : '#000',
                    border: '1px solid',
                    borderColor: darkMode ? '#ff00ff' : '#ccc',
                    padding: 2,
                    mb: 2,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                }}
            >
                {queryText ?? 'Query will appear here after execution.'}
            </Box>
                </div>
            </div>
            <Typography
                variant="h6"
                gutterBottom
                sx={{
                    color: darkMode ? '#ff00ff' : '#333',
                    textShadow: effectsEnabled ? '0 0 5px #ff00ff' : '',
                }}
            >
            
                Terminal Output
            </Typography>

            <Box position="relative" sx={{ mt: 2 }}>
                {queryTriggered && (
                    effectsEnabled ? (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                                zIndex: 1,
                                animation: 'flicker 0.6s infinite'
                            }}
                        >
                            <svg width="0" height="0">
                                <defs>
                                    <filter id="electricGlow" x="-100%" y="-100%" width="300%" height="300%">
                                        <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#00ffff" />
                                        <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#ffffff" />
                                    </filter>
                                </defs>
                            </svg>

                            {/* Top Edge */}
                            <svg width="100%" height="20" viewBox="0 0 400 20" preserveAspectRatio="none"
                                style={{ position: 'absolute', top: 0, left: 0 }}>
                                {[0, 1, 2].map(i => (
                                    <path
                                        key={`top-${i}`}
                                        d={generateLightningPath(400, 20, 10, 12)}
                                        stroke="#00ffff"
                                        strokeWidth="1.5"
                                        fill="none"
                                        filter="url(#electricGlow)"
                                        opacity={0.8 - i * 0.2}
                                    >
                                        <animate
                                            attributeName="stroke-dashoffset"
                                            from="0"
                                            to={100 + i * 50}
                                            dur={`${0.6 + i * 0.2}s`}
                                            repeatCount="indefinite"
                                        />
                                    </path>
                                ))}
                            </svg>

                            {/* Bottom Edge */}
                            <svg width="100%" height="20" viewBox="0 0 400 20" preserveAspectRatio="none"
                                style={{ position: 'absolute', bottom: 0, left: 0 }}>
                                {[0, 1, 2].map(i => (
                                    <path
                                        key={`bottom-${i}`}
                                        d={generateLightningPath(400, 20, 10, 12)}
                                        stroke="#00ffff"
                                        strokeWidth="1.5"
                                        fill="none"
                                        filter="url(#electricGlow)"
                                        opacity={0.8 - i * 0.2}
                                    >
                                        <animate
                                            attributeName="stroke-dashoffset"
                                            from="0"
                                            to={100 + i * 50}
                                            dur={`${0.6 + i * 0.2}s`}
                                            repeatCount="indefinite"
                                        />
                                    </path>
                                ))}
                            </svg>

                            {/* Left Edge */}
                            <svg width="20" height="100%" viewBox="0 0 20 400" preserveAspectRatio="none"
                                style={{ position: 'absolute', top: 0, left: 0 }}>
                                {[0, 1, 2].map(i => (
                                    <path
                                        key={`left-${i}`}
                                        d={generateVerticalLightningPath(400, 20, 10, 12)}
                                        stroke="#00ffff"
                                        strokeWidth="1.5"
                                        fill="none"
                                        filter="url(#electricGlow)"
                                        opacity={0.8 - i * 0.2}
                                    >
                                        <animate
                                            attributeName="stroke-dashoffset"
                                            from="0"
                                            to={100 + i * 50}
                                            dur={`${0.6 + i * 0.2}s`}
                                            repeatCount="indefinite"
                                        />
                                    </path>
                                ))}
                            </svg>

                            {/* Right Edge */}
                            <svg width="20" height="100%" viewBox="0 0 20 400" preserveAspectRatio="none"
                                style={{ position: 'absolute', top: 0, right: 0 }}>
                                {[0, 1, 2].map(i => (
                                    <path
                                        key={`right-${i}`}
                                        d={generateVerticalLightningPath(400, 20, 10, 12)}
                                        stroke="#00ffff"
                                        strokeWidth="1.5"
                                        fill="none"
                                        filter="url(#electricGlow)"
                                        opacity={0.8 - i * 0.2}
                                    >
                                        <animate
                                            attributeName="stroke-dashoffset"
                                            from="0"
                                            to={100 + i * 50}
                                            dur={`${0.6 + i * 0.2}s`}
                                            repeatCount="indefinite"
                                        />
                                    </path>
                                ))}
                            </svg>
                        </Box>
                    ) : (


                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    border: '6px solid #ccc',
                                    borderTop: '6px solid #00ffff',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    animation: 'spin 1s linear infinite',
                                }}
                            />
                            <GlobalStyles styles={{
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' },
                                }
                            }} />
                        </Box>
                    )
                )}
                <Box
                    ref={outputRef}
                    sx={{
                        backgroundColor: darkMode ? '#1a1a1a' : '#f0f0f0',
                        border: '1px solid',
                        borderColor: darkMode ? '#ff00ff' : '#ccc',
                        color: darkMode ? '#00ffff' : '#333',
                        boxShadow: effectsEnabled ? '0 0 10px #00ffff' : 'none',
                        padding: '12px',
                        fontFamily: 'Courier New, monospace',
                        whiteSpace: 'pre-wrap',
                        overflowY: 'auto',
                        maxHeight: '600px',
                        position: 'relative',
                        zIndex: 0
                    }}
                >

                    {terminalOutput.map((entry, index) => {
                        if (entry.type === 'header' || entry.type === 'message') {
                            return (
                                <Typography key={index} sx={{ color: darkMode ? '#00ffff' : '#000', whiteSpace: 'pre-wrap' }}>
                                    {entry.content}
                                </Typography>
                            );
                        } else if (entry.type === 'table') {
                            const rows = entry.content;
                            const columns = Object.keys(rows[0] || {});
                            return (
                                <Table key={index} size="small" sx={{ backgroundColor: darkMode ? '#1a1a1a' : '#fff', color: darkMode ? '#00ffff' : '#000', mb: 2 }}>
                                    <TableHead>
                                        <TableRow>
                                            {columns.map((col, i) => (
                                                <TableCell key={i} sx={{ color: darkMode ? '#ff00ff' : '#000' }}>{col}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row, i) => (
                                            <TableRow key={i}>
                                                {columns.map((col, j) => (
                                                    <TableCell key={j} sx={{ color: darkMode ? '#00ffff' : '#000' }}>{String(row[col])}</TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            );
                        }
                    })}

                </Box>
            </Box>
        </Box>
    );
};

export default SprocConsole;
